import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, AlertTriangle, ChevronDown, RotateCcw } from 'lucide-react';
import { Modal } from '../Modal';
import { api, getToken } from '../../lib/api';
import { useCameraAparelho, useTemCamera } from '../../lib/useCameraAparelho';

// PECA 019 do Catalogo de Componentes — "Reconhecer produto pela camera" (DA-392).
//
// O problema nao e imagem, e VOCABULARIO. O cliente chega no balcao com uma peca na mao, a
// vendedora nao sabe como a loja cadastrou aquilo, e a busca por texto so serve pra quem ja
// sabe o nome. Esta peca e uma PORTA ALTERNATIVA pro mesmo campo de busca — nunca o
// substitui: "nao e nenhum desses? feche e digite o nome" fica sempre visivel.
//
// Ligar numa tela nova custa uma linha:
//   <ReconhecerProdutoCamera tela="orcamento" somenteVendaveis onEscolher={adicionarProduto} />
//
// TRES ACHADOS DA FASE 0 QUE MANDAM NO DESENHO DESTE ARQUIVO:
//
//  1. A camera acerta a FAMILIA e nao le a MEDIDA. Medido: PINO 2P+T 20A -> o mais parecido
//     e o 10A; CHAVE GRIFO 10 -> a 12; 1/2 LUVA KRONA -> a 3/4. E limite do metodo, nao bug.
//     Por isso a tela MOSTRA candidatos e NUNCA crava um — e o aviso da medida e fixo.
//  2. O score e termometro de confianca, mas so em termos RELATIVOS (o corte de 90% do
//     melhor vive no backend). Sem ninguem acima do corte, a resposta e "nao reconheci",
//     nunca uma lista de palpites ruins.
//  3. 231 produtos ativos dividem a foto com outro (o maior grupo tem 29 na MESMA imagem).
//     Onde a foto e literalmente a mesma, nenhum reconhecimento visual pode escolher —
//     entao a lista agrupa pela PECA e deixa a bitola como a unica coisa a decidir.
//     Repetir a mesma imagem 29 vezes empurraria a decisao sem ajudar.

// Formato canonico do produto — o MESMO do `GET /api/produtos` que a busca por texto usa.
// De proposito: a rota de reconhecimento devolve so `produto_id` + score, e as fichas vem
// da consulta de sempre. Assim nao existe um segundo endpoint alimentando esta interface.
export interface ProdutoReconhecido {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  unidade_sigla: string | null;
  marca_nome: string | null;
  grupo_nome: string | null;
  permite_fracionado: boolean;
  estoque_atual: number | null;
  estoque_efetivo: number | string | null;
  localizacao: string | null;
  preco_cartao: number | string;
  preco_pix: number | string;
}

interface Candidato {
  produto_id: number;
  foto_url: string;
  score: number;
  posicao: number;
  forte: boolean;
}

interface RespostaBusca {
  consulta_id: number | null;
  modelo: string;
  duracao_ms: number;
  candidatos: Candidato[];
}

interface Props {
  /** De qual tela partiu — entra na medicao pra separar o acerto do balcao do acerto do estoque. */
  tela: string;
  /** Esconde Uso/Consumo (o fluxo de venda nao os oferece). */
  somenteVendaveis?: boolean;
  /** Chamado quando o operador escolhe. Recebe a ficha completa do produto. */
  onEscolher: (produto: ProdutoReconhecido) => void;
  /**
   * 'no-campo' = botao absoluto no canto direito de um campo de busca (o pai precisa ser
   * `position: relative`). 'solto' = botao normal no fluxo.
   */
  posicao?: 'no-campo' | 'solto';
  titulo?: string;
}

type Fase = 'fechado' | 'camera' | 'lendo' | 'resultado';

interface GrupoPeca {
  chave: string;
  produtos: ProdutoReconhecido[];
  melhorPosicao: number;
}

const money = (v: number | string | null) =>
  'R$ ' + Number(v ?? 0).toFixed(2).replace('.', ',');

// A medida vem no COMECO da descricao ("32 REGISTRO ESFERA SOLD KRONA"). Realcar o prefixo
// numerico e o que faz o olho cair no unico dado que a camera nao leu.
function partirMedida(desc: string): { medida: string | null; resto: string } {
  const limpo = desc.trim();
  const m = limpo.match(/^([\d]+[\d/.,xX-]*)\s+(.*)$/);
  if (!m?.[1] || !m[2]) return { medida: null, resto: limpo };
  return { medida: m[1], resto: m[2] };
}

// Agrupar por ARQUIVO de foto nao bastaria: as bitolas 20/25/50 do registro esfera dividem
// uma imagem e a 32 tem arquivo proprio — pra quem olha, e tudo o mesmo registro Krona.
// A chave e a descricao SEM o prefixo de medida, mais a marca.
function chaveDaPeca(p: ProdutoReconhecido): string {
  return `${partirMedida(p.descricao).resto}|${p.marca_nome ?? ''}`;
}

function agrupar(produtos: ProdutoReconhecido[], posicaoDe: Map<number, number>): GrupoPeca[] {
  const mapa = new Map<string, GrupoPeca>();
  for (const p of produtos) {
    const k = chaveDaPeca(p);
    const g = mapa.get(k);
    const pos = posicaoDe.get(p.id) ?? 999;
    if (g) {
      g.produtos.push(p);
      g.melhorPosicao = Math.min(g.melhorPosicao, pos);
    } else {
      mapa.set(k, { chave: k, produtos: [p], melhorPosicao: pos });
    }
  }
  // O grupo herda a MELHOR posicao de seus produtos — senao a peca certa poderia cair pro
  // fim da lista so porque uma de suas bitolas pontuou mal.
  return [...mapa.values()].sort((a, b) => a.melhorPosicao - b.melhorPosicao);
}

// Ordena as bitolas pelo numero, nao pelo texto: "5" tem que vir antes de "20".
function ordenarPorMedida(produtos: ProdutoReconhecido[]): ProdutoReconhecido[] {
  return [...produtos].sort((a, b) => {
    const na = parseFloat(partirMedida(a.descricao).medida ?? '');
    const nb = parseFloat(partirMedida(b.descricao).medida ?? '');
    if (Number.isNaN(na) || Number.isNaN(nb)) return a.descricao.localeCompare(b.descricao);
    return na - nb;
  });
}

export function ReconhecerProdutoCamera({
  tela,
  somenteVendaveis = false,
  onEscolher,
  posicao = 'no-campo',
  titulo = 'Mostrar produto à câmera',
}: Props) {
  const temCamera = useTemCamera();
  const [disponivel, setDisponivel] = useState<boolean | null>(null);
  const [fase, setFase] = useState<Fase>('fechado');
  const [erro, setErro] = useState('');
  const [consultaId, setConsultaId] = useState<number | null>(null);
  const [fortes, setFortes] = useState<GrupoPeca[]>([]);
  const [fracos, setFracos] = useState<GrupoPeca[]>([]);
  const [verFracos, setVerFracos] = useState(false);
  // Guarda se a consulta ja foi respondida com ESCOLHA — a resposta definitiva.
  const respondidaRef = useRef(false);
  // A desistencia sai no maximo uma vez, mas NAO tranca a peca: se a operadora voltar e
  // escolher um produto, a escolha sobrescreve a desistencia no banco (e por isso o UPDATE
  // da escolha zera o `desistiu`).
  const desistenciaRef = useRef(false);
  // Os ouvintes de saida sao registrados uma vez so — sem o ref eles enxergariam pra sempre
  // o `consultaId` do primeiro render (null) e nunca registrariam nada.
  const consultaIdRef = useRef<number | null>(null);
  useEffect(() => { consultaIdRef.current = consultaId; }, [consultaId]);

  const camera = useCameraAparelho({ facingMode: 'environment', ativa: fase === 'camera' });

  // O motor pode estar fora do ar ou com o indice ainda vazio. Botao que so avisa
  // "indisponivel" DEPOIS do clique gasta a confianca do operador — entao a peca inteira
  // some quando nao ha o que reconhecer.
  useEffect(() => {
    let vivo = true;
    api.get<{ disponivel: boolean }>('/api/reconhecimento/disponivel')
      .then((r) => { if (vivo) setDisponivel(!!r.disponivel); })
      .catch(() => { if (vivo) setDisponivel(false); });
    return () => { vivo = false; };
  }, []);

  // Distingue "ainda nao respondeu" de "olhou e nenhum servia". Sem isso o silencio e ambiguo
  // e a medicao mentiria pra cima, contando so os acertos. Falhar aqui e irrelevante pro
  // operador — nao avisa nada.
  //
  // Precisa SOBREVIVER a pagina indo embora: um `fetch` comum disparado no desmonte ou no
  // pagehide e cancelado junto com a navegacao. Foi exatamente isso que deixou as duas
  // primeiras consultas de producao sem resposta nenhuma — nem escolha, nem desistencia —
  // um terceiro estado que some dos tres vereditos da medicao. `keepalive` deixa a
  // requisicao terminar depois da tela sair; `sendBeacon` nao serve porque nao leva header
  // de autenticacao.
  const registrarDesistencia = useCallback((id: number | null) => {
    if (!id || respondidaRef.current || desistenciaRef.current) return;
    desistenciaRef.current = true;
    const token = getToken();
    void fetch(`/api/reconhecimento/${id}/escolha`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ desistiu: true }),
    }).catch(() => {});
  }, []);

  // A camera sai de cena por caminhos que nao passam pelo X: navegar pra outra tela (desmonta
  // a peca) e fechar a aba. Nos dois a desistencia so e registrada aqui.
  //
  // `visibilitychange` NAO entra, e a tentacao e grande: ele pegaria tambem o tablet dormindo.
  // O problema e que TROCAR DE APLICATIVO dispara igual, e trocar de app nao e desistir. Na
  // primeira consulta real de balcao (grampo U, acerto na posicao 1, unico acima do corte) o
  // operador saiu do navegador por alguns segundos e a consulta foi gravada como desistencia
  // — um acerto perfeito contado como fracasso. Desistencia falsa e pior que silencio:
  // silencio aparece no `sem_resposta` do /estado, desistencia falsa entra num dos tres
  // vereditos e empurra a leitura pro lado contrario ao da verdade. Consulta que o operador
  // deixa pra tras sem fechar fica em `sem_resposta`, que e honestamente o que ela e.
  useEffect(() => {
    const registrar = () => registrarDesistencia(consultaIdRef.current);
    // `pagehide` e nao `beforeunload`: e o unico que dispara no celular e com bfcache.
    window.addEventListener('pagehide', registrar);
    return () => {
      window.removeEventListener('pagehide', registrar);
      registrar();
    };
  }, [registrarDesistencia]);

  const fechar = useCallback(() => {
    registrarDesistencia(consultaId);
    setFase('fechado');
    setErro('');
    setFortes([]);
    setFracos([]);
    setVerFracos(false);
    setConsultaId(null);
  }, [consultaId, registrarDesistencia]);

  const abrir = useCallback(() => {
    respondidaRef.current = false;
    desistenciaRef.current = false;
    setErro('');
    setFortes([]);
    setFracos([]);
    setVerFracos(false);
    setConsultaId(null);
    setFase('camera');
  }, []);

  // "Tirar outra foto" abandona a consulta anterior — e no caminho do "nao reconheci" isso e
  // literalmente "olhou e nenhum servia", que e o sinal do veredito de muita desistencia.
  // Sem registrar aqui, a tentativa frustrada some da medicao.
  const tirarOutra = useCallback(() => {
    registrarDesistencia(consultaIdRef.current);
    abrir();
  }, [registrarDesistencia, abrir]);

  async function tirarEBuscar() {
    setErro('');
    const blob = await camera.tirarFoto();
    if (!blob) {
      setErro('A câmera ainda não entregou imagem. Espere um instante e tente de novo.');
      return;
    }
    setFase('lendo');
    try {
      const fd = new FormData();
      fd.append('foto', blob, 'consulta.jpg');
      fd.append('tela', tela);
      if (somenteVendaveis) fd.append('somente_vendaveis', 'true');
      const token = localStorage.getItem('fiodecobre.token');
      const res = await fetch('/api/reconhecimento/buscar', {
        method: 'POST',
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({} as { erro?: string }));
        throw new Error(d.erro || 'não consegui consultar o reconhecimento');
      }
      const r = (await res.json()) as RespostaBusca;
      setConsultaId(r.consulta_id);
      consultaIdRef.current = r.consulta_id;
      respondidaRef.current = false;
      desistenciaRef.current = false;

      if (!r.candidatos.length) {
        setFortes([]);
        setFracos([]);
        setFase('resultado');
        return;
      }

      // As FICHAS vem da consulta de sempre (`GET /api/produtos`), nao de um SELECT proprio
      // do reconhecimento — mesma tela, mesmos campos, mesma verdade.
      const ids = r.candidatos.map((c) => c.produto_id);
      const fichas = await api.get<ProdutoReconhecido[]>('/api/produtos', {
        query: { ids: ids.join(','), ...(somenteVendaveis ? { somente_vendaveis: true } : {}) },
      });
      const porId = new Map(fichas.map((f) => [f.id, f]));
      const posicaoDe = new Map(r.candidatos.map((c) => [c.produto_id, c.posicao]));

      const pegar = (forte: boolean) =>
        r.candidatos
          .filter((c) => c.forte === forte)
          .map((c) => porId.get(c.produto_id))
          .filter((p): p is ProdutoReconhecido => !!p);

      setFortes(agrupar(pegar(true), posicaoDe));
      setFracos(agrupar(pegar(false), posicaoDe));
      setFase('resultado');
    } catch (e) {
      setErro((e as Error).message);
      setFase('camera');
    }
  }

  function escolher(p: ProdutoReconhecido) {
    if (consultaId && !respondidaRef.current) {
      respondidaRef.current = true;
      // A resposta que importa: em QUE POSICAO estava o certo. Uma semana de balcao vale
      // mais que 15 fotos encenadas — e e o que substitui o teste que pulamos na Fase 0.
      void api.post(`/api/reconhecimento/${consultaId}/escolha`, { produto_id: p.id }).catch(() => {});
    }
    onEscolher(p);
    setFase('fechado');
    setFortes([]);
    setFracos([]);
    setVerFracos(false);
    setConsultaId(null);
  }

  const gruposVisiveis = useMemo(
    () => (verFracos ? [...fortes, ...fracos] : fortes),
    [fortes, fracos, verFracos],
  );

  // Aparelho sem camera ou motor sem indice: a peca inteira nao existe.
  if (temCamera === false || disponivel === false) return null;

  // So o icone, fundo transparente, traco azul — nas DUAS posicoes (escolha do Ruan,
  // 2026-07-29). O alvo do dedo NAO encolhe junto com o desenho: 32/36px de lado, a mesma
  // area do botao solido anterior. No tablet e o alvo que importa, nao o icone.
  const estiloBotao: React.CSSProperties = posicao === 'no-campo'
    ? {
        position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)',
        width: 32, height: 32, borderRadius: 5, display: 'flex',
        alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        background: 'transparent', color: 'var(--primary)', border: 'none',
      }
    : {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 36, height: 36, borderRadius: 6, cursor: 'pointer',
        background: 'transparent', color: 'var(--primary)', border: 'none', flex: '0 0 auto',
      };

  return (
    <>
      <button
        type="button"
        data-componente="019"
        onClick={abrir}
        title="Não sabe o nome? Mostre a peça à câmera"
        aria-label="Buscar produto pela câmera"
        style={estiloBotao}
      >
        <Camera size={posicao === 'no-campo' ? 19 : 22} />
      </button>

      <Modal
        aberto={fase !== 'fechado'}
        onFechar={fechar}
        titulo={fase === 'resultado' ? 'Produtos parecidos' : titulo}
        largura={720}
        // Sair da lista so existia pelo X e pelo Esc — e o tablet do balcao nao tem Esc, entao
        // o X virava ponto unico de falha: quem nao reconheceu nenhum candidato ficava preso e
        // recarregava a tela. O rodape fica FIXO (nao rola junto com a lista), pela mesma razao
        // que todo dblclick do sistema tem um botao de clique unico ao lado. A fase da camera
        // ja tem o "Cancelar" no corpo.
        rodape={fase === 'resultado' ? (
          <button type="button" className="btn-secundario" onClick={fechar}>Fechar</button>
        ) : undefined}
      >
        {(fase === 'camera' || fase === 'lendo') && (
          <div>
            <div style={{ position: 'relative', background: '#0f1d3a', borderRadius: 8, overflow: 'hidden', height: 380 }}>
              <video
                ref={camera.videoRef}
                playsInline
                muted
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
              {/* Moldura de mira: nao recorta nada (o motor le a imagem inteira), so ensina
                  a distancia certa. O fundo genuinamente nao importa — testamos tirar o
                  fundo antes de comparar e PIOROU o acerto. */}
              <div style={{ position: 'absolute', inset: '5% 25%', border: '2px dashed rgba(255,255,255,.8)', borderRadius: 10, pointerEvents: 'none' }} />
              {fase === 'lendo' && (
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(15,29,58,.72)', color: '#fff', fontWeight: 600, fontSize: 15,
                }}>
                  Procurando no catálogo...
                </div>
              )}
            </div>

            {camera.status === 'erro' || camera.status === 'sem-suporte' ? (
              <div style={{
                marginTop: 12, display: 'flex', gap: 10, alignItems: 'center',
                background: '#f97316', color: '#000', borderRadius: 6, padding: '10px 13px',
                fontSize: 13, fontWeight: 600,
              }}>
                <AlertTriangle size={17} style={{ flex: 'none' }} />
                <span style={{ flex: 1 }}>{camera.erro}</span>
                <button type="button" onClick={camera.tentarDeNovo} className="btn-primario" style={{ flex: '0 0 auto' }}>
                  <RotateCcw size={14} style={{ display: 'inline', marginRight: 5, marginBottom: -2 }} />
                  Tentar de novo
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-muted)' }}>
                Encaixe a peça na moldura. O fundo não importa.
              </div>
            )}

            {erro && (
              <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: 'var(--danger)' }}>{erro}</div>
            )}

            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 9 }}>
              <button type="button" className="btn-secundario" onClick={fechar}>Cancelar</button>
              <button
                type="button"
                className="btn-primario"
                onClick={() => void tirarEBuscar()}
                disabled={camera.status !== 'pronta' || fase === 'lendo'}
              >
                {fase === 'lendo' ? 'Procurando...' : 'Tirar foto'}
              </button>
            </div>
          </div>
        )}

        {fase === 'resultado' && (
          <div>
            {gruposVisiveis.length === 0 ? (
              // A tela do "nao reconheci". Nunca uma lista de palpites ruins: sem ninguem
              // acima do corte, a resposta honesta e devolver o operador pro campo de texto.
              <div style={{ padding: '18px 4px' }}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Não reconheci esta peça.</div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
                  Pode ser um produto sem foto no catálogo, ou a foto saiu difícil de ler.
                  Tente de novo mais perto e com a peça inteira na moldura — ou feche e digite o nome.
                </div>
                <button type="button" className="btn-primario" onClick={tirarOutra}>
                  <RotateCcw size={15} style={{ display: 'inline', marginRight: 6, marginBottom: -2 }} />
                  Tirar outra foto
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
                  <b style={{ fontSize: 15 }}>
                    {gruposVisiveis.length} {gruposVisiveis.length === 1 ? 'produto parecido' : 'produtos parecidos'}
                  </b>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>toque no certo</span>
                </div>

                {/* Aviso NEUTRO com barra de acento, nao laranja: o laranja encostaria no
                    amarelo que aqui e exclusivo da MEDIDA — e verde/vermelho nao existem
                    pro Ruan. Separa por forma, nao por cor. */}
                <div style={{
                  display: 'flex', gap: 9, alignItems: 'center', background: 'var(--primary-soft, #e8f0fd)',
                  color: 'var(--panel-fg)', borderLeft: '4px solid var(--primary)', borderRadius: 6,
                  padding: '10px 13px', fontSize: 13, fontWeight: 600, margin: '0 0 14px',
                }}>
                  <AlertTriangle size={17} style={{ flex: 'none', color: 'var(--primary)' }} />
                  <span>A câmera reconhece o tipo de peça, mas não lê a medida — confira a bitola na peça antes de escolher.</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {gruposVisiveis.map((g) => (
                    <GrupoLinha key={g.chave} grupo={g} onEscolher={escolher} />
                  ))}
                </div>

                {!verFracos && fracos.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setVerFracos(true)}
                    style={{
                      marginTop: 11, background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 13, color: 'var(--primary)', fontWeight: 600, padding: 0,
                    }}
                  >
                    <ChevronDown size={15} style={{ display: 'inline', marginBottom: -3 }} />
                    {' '}ver mais {fracos.length} parecido{fracos.length === 1 ? '' : 's'} (menos prováveis)
                  </button>
                )}

                <div style={{
                  marginTop: 13, paddingTop: 12, borderTop: '1px solid var(--border)',
                  fontSize: 13, color: 'var(--text-muted)',
                }}>
                  Não é nenhum desses? Feche e digite o nome — a busca por texto continua igual.
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

// Uma linha por PECA. Com mais de uma bitola, a foto aparece UMA vez e as medidas viram
// chips clicaveis — repetir a mesma imagem 4 (ou 29) vezes empurraria a decisao pro
// operador sem ajudar em nada.
function GrupoLinha({ grupo, onEscolher }: { grupo: GrupoPeca; onEscolher: (p: ProdutoReconhecido) => void }) {
  const primeiro = grupo.produtos[0];
  if (!primeiro) return null;   // grupo so nasce com pelo menos um produto; guarda do tipo
  const meta = [primeiro.marca_nome, primeiro.grupo_nome].filter(Boolean).join(' · ');
  const unico = grupo.produtos.length === 1;
  const variacoes = ordenarPorMedida(grupo.produtos);
  const { medida, resto } = partirMedida(primeiro.descricao);

  const caixa: React.CSSProperties = {
    display: 'flex', alignItems: unico ? 'center' : 'flex-start', gap: 14,
    background: 'var(--popup-bg, #fff)', border: '1px solid var(--border)',
    borderRadius: 8, padding: unico ? '10px 13px' : '12px 13px', width: '100%',
    textAlign: 'left', cursor: unico ? 'pointer' : 'default',
  };

  const foto = primeiro.foto_url ? (
    <img
      src={primeiro.foto_url}
      alt=""
      style={{
        width: 76, height: 76, objectFit: 'contain', background: '#fff',
        borderRadius: 5, flex: 'none', border: '1px solid var(--border)',
      }}
    />
  ) : (
    <div style={{ width: 76, height: 76, flex: 'none', borderRadius: 5, background: 'var(--input-bg)' }} />
  );

  if (unico) {
    return (
      <button type="button" onClick={() => onEscolher(primeiro)} style={caixa}>
        {foto}
        <div style={{ minWidth: 0 }}>
          <div className="cel-num" style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 700 }}>
            {primeiro.codigo_interno}
          </div>
          <div style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.25, marginTop: 2 }}>
            {/* O amarelo e EXCLUSIVO da medida — e o numero que o olho tem de cacar. */}
            {medida && (
              <span style={{ background: '#fde68a', color: '#000', padding: '0 5px', borderRadius: 3 }}>{medida}</span>
            )}{medida ? ' ' : ''}{resto}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{meta}</div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right', flex: 'none' }}>
          <div className="cel-num" style={{ fontSize: 17, fontWeight: 700, color: 'var(--primary)' }}>
            {money(primeiro.preco_pix)}
          </div>
          <div className="cel-num" style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {primeiro.estoque_atual ?? 0} {primeiro.unidade_sigla || 'UN'} em estoque
          </div>
        </div>
      </button>
    );
  }

  return (
    <div style={caixa}>
      {foto}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.25 }}>{resto}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
          {meta} &nbsp;·&nbsp; {grupo.produtos.length} medidas dessa mesma peça — escolha qual:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 9 }}>
          {variacoes.map((v) => {
            const m = partirMedida(v.descricao);
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => onEscolher(v)}
                title={v.descricao}
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 7, cursor: 'pointer',
                  border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px',
                  background: 'var(--input-bg)',
                }}
              >
                <span className="cel-num" style={{
                  background: '#fde68a', color: '#000', fontWeight: 700, fontSize: 14,
                  padding: '1px 6px', borderRadius: 3,
                }}>
                  {m.medida ?? v.descricao}
                </span>
                <span className="cel-num" style={{ color: 'var(--primary)', fontWeight: 700, fontSize: 14 }}>
                  {money(v.preco_pix)}
                </span>
                <span className="cel-num" style={{ color: 'var(--text-muted)', fontSize: 11.5 }}>
                  {v.estoque_atual ?? 0} {v.unidade_sigla || 'UN'}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
